import React, { useRef } from 'react';
import AvatarEditor from 'react-avatar-editor';
import {
  ChakraBox,
  ChakraFlex,
  ChakraSlider,
  ChakraSliderFilledTrack,
  ChakraSliderThumb,
  ChakraSliderTrack,
  FAIconName,
  FireflyButton,
  Icon,
} from '@equip.health/ui';

interface AvatarEditorProps {
  file: File;
  onBack: () => void;
  onPreview: (croppedImageUrl: string) => void;
  scale: number;
  setScale: React.Dispatch<React.SetStateAction<number>>;
  position: { x: number; y: number };
  setPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
}

const TOP_LEVEL_ID = 'avatar-upload';

const AvatarImageEditor = ({
  file,
  onBack,
  onPreview,
  scale,
  setScale,
  position,
  setPosition,
}: AvatarEditorProps) => {
  const editorRef = useRef<AvatarEditor | null>(null);

  const handlePositionChange = (position: { x: number; y: number }) => {
    setPosition(position);
  };

  const handleZoom = (increment: number) => {
    setScale((prevScale) => Math.max(1, Math.min(prevScale + increment, 3)));
  };

  const handleSliderChange = (value: number) => {
    setScale(value);
  };

  const handlePreview = () => {
    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas();
      const croppedImageUrl = canvas.toDataURL();
      onPreview(croppedImageUrl);
    }
  };

  return (
    <>
      <ChakraFlex direction="column" gap="32px" padding="15px 0px 32px 0px">
        <ChakraBox display="flex" height="300px" justifyContent="center">
          <AvatarEditor
            border={50}
            borderRadius={100}
            color={[0, 0, 0, 0.3]}
            image={file}
            onPositionChange={handlePositionChange}
            position={position}
            ref={editorRef}
            rotate={0}
            scale={scale}
            style={{
              height: 'auto',
              maxWidth: '300px',
              width: 'auto',
            }}
          />
        </ChakraBox>
        <ChakraBox display="flex" justifyContent="center">
          <ChakraFlex gap="16px" maxWidth="300px" width="100%">
            <ChakraBox
              color="primary.90"
              cursor="pointer"
              id={`${TOP_LEVEL_ID}__minus-button`}
              onClick={() => handleZoom(-0.1)}
            >
              <Icon
                aria-label={FAIconName.minusRegular}
                name={FAIconName.minusRegular}
              />
            </ChakraBox>
            <ChakraSlider
              aria-label="zoom-slider"
              flex={1}
              max={3}
              min={1}
              onChange={handleSliderChange}
              step={0.01}
              value={scale}
            >
              <ChakraSliderTrack bg="neutral.line">
                <ChakraSliderFilledTrack bg="primary.100" />
              </ChakraSliderTrack>
              <ChakraSliderThumb bg="primary.100" />
            </ChakraSlider>
            <ChakraBox
              color="primary.90"
              cursor="pointer"
              id={`${TOP_LEVEL_ID}__plus-button`}
              onClick={() => handleZoom(0.1)}
            >
              <Icon
                aria-label={FAIconName.plusRegular}
                name={FAIconName.plusRegular}
              />
            </ChakraBox>
          </ChakraFlex>
        </ChakraBox>
      </ChakraFlex>
      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <FireflyButton
          id={`${TOP_LEVEL_ID}__left-button`}
          leftIcon={FAIconName.arrowLeftRegular}
          onClick={onBack}
          variant="secondary"
        >
          Back
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__right-button`}
          onClick={handlePreview}
          rightIcon={FAIconName.chevronRightRegular}
          variant="primary"
        >
          Preview
        </FireflyButton>
      </ChakraFlex>
    </>
  );
};

export default AvatarImageEditor;
